const normalizeOld = (v?: number | null) => v ?? undefined

import type {
  BrandWarrantyQuotationItemDto,
  ItemPriceOnlyDto,
  MachineAttachmentItemDto,
  MachineQuotationItemDto,
  WarrantyDto
} from '@api/machines'
import {
  displayOldPrice,
  formattedTransportType,
  type ServiceContractType
} from '@common/utils'
import { useTranslation } from 'react-i18next'
import { BreakdownItem } from '../breakdown-item/BreakdownItem'
import { BreakdownSection } from '../breakdown-section/BreakdownSection'

export type ServicesSectionProps = {
  hasServiceContracts?: boolean
  keyPrefix?: string
  machineQuotationItem: MachineAttachmentItemDto & MachineQuotationItemDto
  selectedServiceContract?: ServiceContractType
  servicesOldPrices?: {
    serviceContract?: ItemPriceOnlyDto
    transportOption?: ItemPriceOnlyDto
    warrantyItem?: ItemPriceOnlyDto
  }
  useBrandWarranty?: boolean
}

export const ServicesSection = ({
  hasServiceContracts,
  keyPrefix = 'common',
  machineQuotationItem,
  selectedServiceContract,
  servicesOldPrices,
  useBrandWarranty = false
}: ServicesSectionProps) => {
  const { t } = useTranslation('', { keyPrefix })

  const {
    machineAttachments,
    serviceContract,
    transportOption,
    warrantyItem: warrantyItemAux,
    brandWarrantyItem
  } = machineQuotationItem

  const warrantyItem = (
    useBrandWarranty ? brandWarrantyItem : warrantyItemAux
  ) as WarrantyDto & BrandWarrantyQuotationItemDto

  const calculateSectionTotal = (current: number[]) =>
    current.reduce((acc, val) => acc + val, 0)

  const getCurrentPrices = () => [
    warrantyItem?.dnp ?? 0,
    serviceContract?.isArchived ? 0 : (serviceContract?.dnp ?? 0),
    transportOption?.dnp ?? 0
  ]

  const getOldPrices = () => [
    normalizeOld(servicesOldPrices?.warrantyItem?.dnp),
    serviceContract?.isArchived
      ? undefined
      : normalizeOld(servicesOldPrices?.serviceContract?.dnp),
    normalizeOld(servicesOldPrices?.transportOption?.dnp)
  ]

  // Section current total should always reflect sum of all current (unarchived) items
  const currentPrices = getCurrentPrices()
  const oldPrices = getOldPrices()
  const indicesWithOld = oldPrices
    .map((v, idx) => (v !== undefined ? idx : -1))
    .filter(idx => idx !== -1)
  const sectionTotalCurrentPrice = calculateSectionTotal(currentPrices)
  const sectionTotalOldPrice =
    indicesWithOld.length > 0
      ? calculateSectionTotal(
          indicesWithOld.map(idx => oldPrices[idx] as number)
        )
      : undefined

  const countUnarchivedServicesItems = () => {
    const items = [
      warrantyItem && !warrantyItem?.isArchived,
      !serviceContract?.isArchived && (serviceContract || hasServiceContracts),
      machineQuotationItem.isTransportOptionFitted ||
        (transportOption && !transportOption?.isArchived)
    ]
    return items.filter(Boolean).length
  }

  const renderWarrantyItem = () =>
    warrantyItem &&
    !warrantyItem?.isArchived && (
      <BreakdownItem
        name={`${t('warranty')} - ${warrantyItem?.typeOfWarranty} ${warrantyItem?.cover ?? ''} (${warrantyItem?.hours}h/${warrantyItem?.months}m)`}
        oldPrice={displayOldPrice(
          warrantyItem?.dnp ?? 0,
          normalizeOld(servicesOldPrices?.warrantyItem?.dnp)
        )}
        price={warrantyItem?.dnp ?? 0}
      />
    )

  const renderServiceContract = () => {
    if (serviceContract && !serviceContract.isArchived) {
      return (
        <BreakdownItem
          name={`${t('serviceContract')} - ${serviceContract.type} - ${serviceContract.hours}hrs`}
          oldPrice={displayOldPrice(
            serviceContract.dnp ?? 0,
            normalizeOld(servicesOldPrices?.serviceContract?.dnp)
          )}
          price={serviceContract.dnp ?? 0}
        />
      )
    } else if (hasServiceContracts) {
      return (
        <BreakdownItem
          name={`${t('serviceContract')} - ${selectedServiceContract}`}
          price={0}
        />
      )
    }
    return null
  }

  const renderTransportItem = () => {
    if (machineQuotationItem.isTransportOptionFitted) {
      return <BreakdownItem name={t('transportFitted')} price={0} />
    }

    return (
      transportOption &&
      !transportOption?.isArchived && (
        <BreakdownItem
          name={`${t('transport')} - ${formattedTransportType(
            transportOption?.optionType ?? ''
          )} - ${transportOption?.name}`}
          oldPrice={displayOldPrice(
            transportOption?.dnp ?? 0,
            normalizeOld(servicesOldPrices?.transportOption?.dnp)
          )}
          price={transportOption?.dnp ?? 0}
        />
      )
    )
  }

  const unarchivedItemsCount = countUnarchivedServicesItems()

  return unarchivedItemsCount > 0 ? (
    <BreakdownSection
      highlightedSection={
        !!(machineAttachments && machineAttachments.length > 0)
      }
      oldPrice={displayOldPrice(sectionTotalCurrentPrice, sectionTotalOldPrice)}
      price={sectionTotalCurrentPrice}
      title={`${t('services')} (${unarchivedItemsCount})`}
    >
      {renderWarrantyItem()}
      {renderServiceContract()}
      {renderTransportItem()}
    </BreakdownSection>
  ) : null
}
